import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  OAuthApi,
  AuthRequestOptions,
  configApiRef,
  createApiFactory,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import type { FetchApi } from '@backstage/core-plugin-api';
import {
  cloudbuildApiRef,
  CloudbuildClient,
} from '@backstage-community/plugin-cloudbuild';

/**
 * Fetches GCP access tokens from the backend gcp-token plugin instead of
 * requiring each user to authenticate with Google Cloud via OAuth. The backend
 * uses Application Default Credentials (Workload Identity on GKE).
 */
class BackendGcpAuth implements OAuthApi {
  private readonly fetchApi: FetchApi;
  private cachedToken: string | undefined;
  private tokenExpiry = 0;

  constructor(fetchApi: FetchApi) {
    this.fetchApi = fetchApi;
  }

  async getAccessToken(
    _scope: string | string[],
    _options?: AuthRequestOptions,
  ): Promise<string> {
    if (this.cachedToken && Date.now() < this.tokenExpiry) {
      return this.cachedToken;
    }

    const response = await this.fetchApi.fetch('/api/gcp-token/token');
    if (!response.ok) {
      throw new Error(`Failed to get GCP token: ${response.statusText}`);
    }
    const data = (await response.json()) as { token: string };
    this.cachedToken = data.token;
    // Cache for 50 minutes (GCP tokens are valid for 60 minutes)
    this.tokenExpiry = Date.now() + 50 * 60 * 1000;
    return data.token;
  }
}

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: cloudbuildApiRef,
    deps: { fetchApi: fetchApiRef },
    factory: ({ fetchApi }) =>
      new CloudbuildClient(new BackendGcpAuth(fetchApi)),
  }),
];
